/**
 * Cross-tab authentication synchronisation.
 *
 * Authentication in this app is cookie-based (`withCredentials`), and cookies
 * are shared across every tab of the same origin — but each tab keeps the
 * logged-in identity and organisation in its own in-memory state (the zustand
 * auth store + the React Query cache). When another tab logs out and logs in as
 * a different account it silently replaces the shared cookie, leaving the
 * original tab operating with a stale identity/org context. That mismatch makes
 * the backend authorise requests as the new user while the stale tab still
 * stamps the old `X-Current-Organization`, producing 404/403 responses.
 *
 * This module broadcasts login/logout transitions to the other tabs so they can
 * tear down their stale state and re-bootstrap. It is intentionally decoupled
 * from `BroadcastChannel` and from React: the channel is injected, which keeps
 * the reconciliation logic unit-testable.
 */

/** The kinds of auth transition worth propagating to other tabs. */
export type AuthSyncType = 'login' | 'logout'

/** A message posted on the auth channel. */
export interface AuthSyncMessage {
  type: AuthSyncType
  /** Identifies the tab that originated the message (see {@link getTabId}). */
  origin: string
}

/** The subset of `BroadcastChannel` this module depends on — injectable for tests. */
export interface AuthSyncChannel {
  postMessage(message: any): void
  addEventListener(
    type: 'message',
    listener: (event: { data: any }) => void
  ): void
  removeEventListener(
    type: 'message',
    listener: (event: { data: any }) => void
  ): void
  close(): void
}

const CHANNEL_NAME = 'netbaan-auth-sync'

let tabId = ''

/**
 * A stable identifier for the current tab, generated once. Used to tag outgoing
 * messages so a tab can ignore the echoes of its own broadcasts.
 */
export function getTabId(): string {
  if (!tabId) {
    tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
  return tabId
}

/** Post an auth transition to the other tabs. */
export function broadcastAuthChange(
  channel: AuthSyncChannel,
  type: AuthSyncType
): void {
  channel.postMessage({ type, origin: getTabId() })
}

function isAuthSyncMessage(data: any): data is AuthSyncMessage {
  return (
    !!data &&
    (data.type === 'login' || data.type === 'logout') &&
    typeof data.origin === 'string'
  )
}

/**
 * Subscribe to auth transitions coming from *other* tabs. Messages originating
 * from this same tab, and any malformed/unrelated messages, are ignored.
 *
 * @returns an unsubscribe function.
 */
export function subscribeAuthChange(
  channel: AuthSyncChannel,
  onRemoteChange: (message: AuthSyncMessage) => void
): () => void {
  const listener = (event: { data: any }) => {
    const message = event.data
    if (!isAuthSyncMessage(message)) return
    if (message.origin === getTabId()) return
    onRemoteChange(message)
  }
  channel.addEventListener('message', listener)
  return () => channel.removeEventListener('message', listener)
}

/**
 * Create the real BroadcastChannel used at runtime, or `null` in environments
 * without `BroadcastChannel` support (older browsers, some test runners). All
 * call sites must treat a `null` channel as "cross-tab sync unavailable".
 */
export function createAuthSyncChannel(): AuthSyncChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  return new BroadcastChannel(CHANNEL_NAME)
}

let sharedChannel: AuthSyncChannel | null | undefined

/**
 * The process-wide auth channel for this tab, created lazily on first use and
 * reused thereafter. Returns `null` where BroadcastChannel is unavailable.
 */
export function getAuthSyncChannel(): AuthSyncChannel | null {
  if (sharedChannel === undefined) {
    sharedChannel = createAuthSyncChannel()
  }
  return sharedChannel
}
